import { useCallback, useEffect, useMemo, useState } from "react";
import { buildCoachSubmitBody, canPlayerEnterResultForMatch } from "../utils/kotCoachSubmit";
import {
  defaultLoserScore,
  inferWinnerSideFromScores,
  stopLoserOnlyScores,
} from "../utils/kotCoachScoreMapping";

const DEFAULT_LOSER_SCORE = 9;
const SUBMIT_ACK_TIMEOUT_MS = 15000;

/**
 * Coach match result: 2-step wizard (winner → score + submit).
 * Coach submits close the wizard immediately and track per-table pending state.
 */
export function useKotCoachSubmitResult({
  socket,
  tsId,
  teId,
  snapshot,
  enabled = true,
  onSubmitted,
  submitterMode = "coach",
  viewerPlayerId = null,
}) {
  const [activeMatch, setActiveMatch] = useState(null);
  const [wizardStep, setWizardStep] = useState("winner");
  const [winnerSide, setWinnerSide] = useState(null);
  const [loserScore, setLoserScore] = useState(DEFAULT_LOSER_SCORE);
  const [goldenPointEnabled, setGoldenPointEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingSubmitMatchIds, setPendingSubmitMatchIds] = useState({});
  const [submitErrorsByMatchId, setSubmitErrorsByMatchId] = useState({});
  const [error, setError] = useState(null);
  const [roundStateOverride, setRoundStateOverride] = useState(null);

  const isCoachSubmitter = submitterMode === "coach";

  const submitConfig = useMemo(() => {
    const cfg = snapshot?.config || {};
    return {
      ...cfg,
      round_state:
        roundStateOverride ?? snapshot?.current_round?.round_state ?? null,
    };
  }, [snapshot, roundStateOverride]);

  // Coach optimistic UI clears on socket ack; if the ack is lost, reconcile from
  // `kot:match-submitted` / snapshot when the match row becomes WON.
  useEffect(() => {
    const matches = snapshot?.matches;
    if (!Array.isArray(matches) || matches.length === 0) return;
    setPendingSubmitMatchIds((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      let changed = false;
      const next = { ...prev };
      for (const matchId of keys) {
        const row = matches.find((m) => String(m.kot_match_id) === matchId);
        if (row && (row.match_state === "WON" || row.match_state === "ABANDONED")) {
          delete next[matchId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setSubmitErrorsByMatchId((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      let changed = false;
      const next = { ...prev };
      for (const matchId of keys) {
        const row = matches.find((m) => String(m.kot_match_id) === matchId);
        if (row && (row.match_state === "WON" || row.match_state === "ABANDONED")) {
          delete next[matchId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [snapshot]);

  const setPointsTarget = Math.max(
    3,
    Number(submitConfig?.set_points_target) || 11
  );
  const setWinBy = Math.max(1, Number(submitConfig?.set_win_by) || 2);

  const resolvedScores = useMemo(() => {
    if (!winnerSide) return { scoreA: 0, scoreB: 0 };
    const winnerIsA = winnerSide === "A";
    return stopLoserOnlyScores({
      winnerIsPlayerA: winnerIsA,
      loserScore: Number(loserScore),
      goldenPoint: goldenPointEnabled,
      target: setPointsTarget,
      winBy: setWinBy,
      useWinnerAtSetTarget: true,
      snapshotScoreA:
        activeMatch?.player_a_score != null
          ? Number(activeMatch.player_a_score)
          : setPointsTarget,
      snapshotScoreB:
        activeMatch?.player_b_score != null
          ? Number(activeMatch.player_b_score)
          : defaultLoserScore(setPointsTarget),
    });
  }, [
    winnerSide,
    loserScore,
    goldenPointEnabled,
    setPointsTarget,
    setWinBy,
    activeMatch,
  ]);

  const initScoresForWinner = useCallback(
    (side, match) => {
      const winnerIsA = side === "A";
      const defLoser = DEFAULT_LOSER_SCORE;
      setLoserScore(defLoser);
      const mapped = stopLoserOnlyScores({
        winnerIsPlayerA: winnerIsA,
        loserScore: defLoser,
        goldenPoint: false,
        target: setPointsTarget,
        winBy: setWinBy,
        useWinnerAtSetTarget: true,
        snapshotScoreA:
          match?.player_a_score != null
            ? Number(match.player_a_score)
            : setPointsTarget,
        snapshotScoreB:
          match?.player_b_score != null
            ? Number(match.player_b_score)
            : defLoser,
      });
      return mapped;
    },
    [setPointsTarget, setWinBy]
  );

  const isMatchSubmitting = useCallback(
    (matchId) =>
      matchId != null && Boolean(pendingSubmitMatchIds[String(matchId)]),
    [pendingSubmitMatchIds]
  );

  const getMatchSubmitError = useCallback(
    (matchId) => {
      if (matchId == null) return null;
      return submitErrorsByMatchId[String(matchId)] || null;
    },
    [submitErrorsByMatchId]
  );

  const clearMatchSubmitError = useCallback((matchId) => {
    if (matchId == null) return;
    setSubmitErrorsByMatchId((prev) => {
      const key = String(matchId);
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const openForMatch = useCallback(
    (match, opts = {}) => {
      if (!match) return;
      if (isMatchSubmitting(match.kot_match_id)) return;
      if (
        submitterMode === "player" &&
        viewerPlayerId &&
        !canPlayerEnterResultForMatch({
          match,
          config: submitConfig,
          roundState: submitConfig?.round_state,
          viewerPlayerId,
        })
      ) {
        return;
      }
      clearMatchSubmitError(match.kot_match_id);
      setRoundStateOverride(opts.roundStateOverride ?? null);
      setError(null);
      setWizardStep("winner");
      setGoldenPointEnabled(false);
      setLoserScore(DEFAULT_LOSER_SCORE);
      const inferred = inferWinnerSideFromScores(
        match.player_a_score,
        match.player_b_score
      );
      setWinnerSide(inferred);
      if (inferred) {
        initScoresForWinner(inferred, match);
      }
      setActiveMatch(match);
    },
    [
      initScoresForWinner,
      submitterMode,
      viewerPlayerId,
      submitConfig,
      isMatchSubmitting,
      clearMatchSubmitError,
    ]
  );

  const close = useCallback(() => {
    if (!isCoachSubmitter && submitting) return;
    setActiveMatch(null);
    setRoundStateOverride(null);
    setError(null);
    setWizardStep("winner");
    setWinnerSide(null);
  }, [submitting, isCoachSubmitter]);

  const pickWinner = useCallback(
    (side) => {
      setWinnerSide(side);
      setError(null);
      setGoldenPointEnabled(false);
      if (activeMatch) initScoresForWinner(side, activeMatch);
      setWizardStep("scores");
    },
    [activeMatch, initScoresForWinner]
  );

  const adjustLoserScore = useCallback((delta) => {
    setLoserScore((prev) => Math.max(0, Math.min(21, prev + delta)));
    setError(null);
  }, []);

  const canSubmit = useMemo(() => {
    if (!winnerSide) return false;
    const { scoreA: a, scoreB: b } = resolvedScores;
    return Number.isFinite(a) && Number.isFinite(b);
  }, [winnerSide, resolvedScores]);

  const wizardBack = useCallback(() => {
    setError(null);
    if (wizardStep === "scores") setWizardStep("winner");
  }, [wizardStep]);

  const resetWizardState = useCallback(() => {
    setActiveMatch(null);
    setRoundStateOverride(null);
    setWizardStep("winner");
    setWinnerSide(null);
    setError(null);
  }, []);

  const submitCoachOptimistic = useCallback(
    (match, emitBody) => {
      const matchId = String(match.kot_match_id);
      resetWizardState();
      setPendingSubmitMatchIds((prev) => ({ ...prev, [matchId]: true }));

      let settled = false;
      const clearPending = () => {
        setPendingSubmitMatchIds((prev) => {
          if (!prev[matchId]) return prev;
          const next = { ...prev };
          delete next[matchId];
          return next;
        });
      };

      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        clearPending();
        if (onSubmitted) onSubmitted();
      }, SUBMIT_ACK_TIMEOUT_MS);

      socket.emit("kot:submit-result", emitBody, (ack) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        clearPending();

        if (!ack || ack.ok === false) {
          setSubmitErrorsByMatchId((prev) => ({
            ...prev,
            [matchId]: ack?.message || ack?.code || "kotCoachRescueFailed",
          }));
          return;
        }

        setSubmitErrorsByMatchId((prev) => {
          if (!prev[matchId]) return prev;
          const next = { ...prev };
          delete next[matchId];
          return next;
        });
        if (onSubmitted) onSubmitted();
      });
    },
    [socket, resetWizardState, onSubmitted]
  );

  const submit = useCallback(() => {
    if (!enabled || !socket || !tsId || !teId || !activeMatch) return;

    const { scoreA: a, scoreB: b } = resolvedScores;
    const payload = buildCoachSubmitBody(
      activeMatch,
      submitConfig,
      a,
      b,
      winnerSide || "A"
    );

    if (!payload) {
      setError("kotCoachRescueInvalid");
      return;
    }

    setError(null);

    const emitBody = {
      ts_id: tsId,
      te_id: teId,
      kot_round_id: activeMatch.kot_round_id,
      kot_match_id: activeMatch.kot_match_id,
      table_id: activeMatch.table_id,
      winner_player_id: payload.winner_player_id,
      player_a_score: payload.player_a_score,
      player_b_score: payload.player_b_score,
      decided_by: payload.decided_by,
    };
    if (submitterMode === "tableCamera") {
      // The server allows this only when the socket is the camera for the table.
      emitBody.submitter_is_coach = false;
      if (viewerPlayerId) {
        emitBody.submitted_by_player_id = String(viewerPlayerId);
      }
    } else if (submitterMode === "player" && viewerPlayerId) {
      emitBody.submitter_is_coach = false;
      emitBody.submitted_by_player_id = String(viewerPlayerId);
    } else {
      emitBody.submitter_is_coach = true;
    }

    if (isCoachSubmitter) {
      submitCoachOptimistic(activeMatch, emitBody);
      return;
    }

    setSubmitting(true);
    socket.emit("kot:submit-result", emitBody, (ack) => {
      setSubmitting(false);
      if (!ack || ack.ok === false) {
        setError(ack?.message || ack?.code || "kotCoachRescueFailed");
        return;
      }
      resetWizardState();
      if (onSubmitted) onSubmitted();
    });
  }, [
    enabled,
    socket,
    tsId,
    teId,
    activeMatch,
    submitConfig,
    resolvedScores,
    winnerSide,
    submitterMode,
    viewerPlayerId,
    isCoachSubmitter,
    submitCoachOptimistic,
    resetWizardState,
    onSubmitted,
  ]);

  return {
    activeMatch,
    wizardStep,
    winnerSide,
    loserScore,
    goldenPointEnabled,
    resolvedScores,
    submitting,
    pendingSubmitMatchIds,
    isMatchSubmitting,
    getMatchSubmitError,
    error,
    submitConfig,
    setGoldenPointEnabled,
    pickWinner,
    adjustLoserScore,
    wizardBack,
    canSubmit,
    openForMatch,
    close,
    submit,
  };
}
